import React, { useEffect, useRef, useState } from "react";
import { App, Card, Button, Typography, Modal, Select, Drawer,Tooltip, Space, Grid } from "antd";
import { ArrowLeftOutlined, PrinterOutlined,  QuestionCircleOutlined, FilePdfOutlined} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import ChequesFilters from "./components/ChequesFilters";
import ChequesTable from "./components/ChequesTable";
import ChequeVoucherBody from "./components/ChequeVoucherBody";
import ReportDom from "../../components/common/ReportDom";
import { generatePdfBlob } from "../../lib/functions";
import {
  fetchChequeList,
  updateChequeStatus,
  fetchChequeAccounts,
  fetchChequeById
} from "./chequesThunk";
import dayjs from "dayjs";

const { Title } = Typography;

const Cheques = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { message } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { list, loading, filters, accounts, accountsLoading,selected } = useSelector(
    (state) => state.cheques
  );

  const [updateModal, setUpdateModal] = useState(false);
  const [selectedCheque, setSelectedCheque] = useState(null);
  const [status, setStatus] = useState("");
  const [account, setAccount] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const reportRef = useRef(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewCheque, setViewCheque] = useState(null);
  const [voucherPdfLoading, setVoucherPdfLoading] = useState(false);
  const voucherReportRef = useRef(null);
  const voucherViewRef = useRef(null);

  useEffect(() => {
    dispatch(fetchChequeList(filters));
    dispatch(fetchChequeAccounts());
  }, [dispatch, filters]);

  const handleUpdate = (cheque) => {
    dispatch(fetchChequeById(cheque.id));
    dispatch(fetchChequeAccounts());
    setSelectedCheque(cheque);
    setStatus("");
    setAccount(null);
    setUpdateModal(true);
  };

  const handleView = (cheque) => {
    setViewCheque(cheque);
    setViewOpen(true);
  };

  const handleVoucherDownload = async () => {
    if (!viewCheque || !voucherViewRef.current) return;
    try {
      setVoucherPdfLoading(true);
      const blob = await generatePdfBlob(voucherViewRef.current);
      if (!blob) throw new Error("PDF generation failed");

      const fileName = `Cheque_${viewCheque.voucher_no || viewCheque.id}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Failed to download voucher");
    } finally {
      setVoucherPdfLoading(false);
    }
  };

  const handleSave = async () => {
    if (!status) {
      message.warning("Please select status");
      return;
    }
    try {
      await dispatch(
        updateChequeStatus({
         chequeId: selected.id,
        status,
        amount: selected.amount,
        to_acc_id: account,
        cus_id: selected.cus_id,
        paid_date: dayjs().format("YYYY-MM-DD"),
        voucher_no: selected.voucher_no,
        voucher_type: selected.voucher_type,
        rcv_d_id: selected.rcv_d_id,
        cheque_acc_id: selected.cheque_acc_id,
        pay_d_id: selected.pay_d_id,
        })
      ).unwrap();
      message.success("Cheque updated ✅");
      setUpdateModal(false);
    } catch {
      message.error("Failed to update cheque ❌");
    }
  };

  const handlePrint = useReactToPrint({

























































    content: () => reportRef.current,
    documentTitle: "Cheque Report",
    removeAfterPrint: true,
  });

  const handleVoucherPrint = useReactToPrint({
    contentRef: voucherReportRef,
    documentTitle: `Cheque Voucher - ${viewCheque?.voucher_no || ""}`,
    removeAfterPrint: true,
  });

  return (
    <div className="p-4">
      <App>
        <Card
          className="shadow-md rounded-xl mb-4"
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Button
                type="text"
                icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
                onClick={() => navigate(-1)}
                data-testid="cheques-back-button"
              />
              <Title level={4} style={{ margin: 0 }}>
                Cheque List
              </Title>

              <div style={{ marginLeft: "auto" }}>
                <Tooltip title="Help">
                  <Button
                    type="text"
                    icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
                    onClick={() => setHelpOpen(true)}
                  />
                </Tooltip>
              </div>
            </div>
          }
          extra={
            <Button
              icon={<PrinterOutlined />}
              type="primary"
              onClick={handlePrint}
            >
              Print
            </Button>
          }
          styles={{ header: { borderBottom: "none" }, body: { padding: 0 } }}
        />

        {/* Filters */}
        <ChequesFilters />

        {/* Table */}
        <Card className="shadow-md rounded-xl">
          <ChequesTable
            list={list}
            loading={loading}
            onUpdate={handleUpdate}
            onView={handleView}
            accounts={accounts}
            accountsLoading={accountsLoading}
          />
        </Card>
      </App>
      <Drawer
        title="Cheque Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Cheque Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Cheque Management module is used to monitor, verify,
            approve, reject, and track cheque transactions within the ERP system.
            It helps manage cheque-based financial operations and reconciliation.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View cheque transaction records</li>
              <li>Filter cheque entries using report filters</li>
              <li>Approve or reject cheque transactions</li>
              <li>Assign cheque transactions to accounts</li>
              <li>Review cheque amounts and voucher details</li>
              <li>Track cheque statuses and transaction history</li>
              <li>Print cheque reports when required</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Cheque approval updates accounting transactions immediately.</li>
              <li>Correct account selection is important during cheque approval.</li>
              <li>Rejected cheques remain recorded for audit tracking.</li>
              <li>Printed reports reflect currently filtered cheque data.</li>
              <li>Cheque transaction history is maintained for financial reporting.</li>
            </ul>
          </div>
        </div>
      </Drawer>

      {/* Hidden Print */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Cheque Report"
          filters={filters}
          renderTable={() => <ChequesTable list={list} printMode />}
        />
      </div>

      {/* Update Modal */}
      <Modal
        open={updateModal}
        title={`Update Cheque - ${selectedCheque?.voucher_no}`}
        onCancel={() => setUpdateModal(false)}
        onOk={handleSave}
        okText="Update"
      >
        <p>
          <b>Amount:</b> {selectedCheque?.amount}
        </p>
        <p>
          <b>Date:</b>{" "}
          {dayjs(selectedCheque?.invoice_date).format("DD MMM YYYY")}
        </p>
        <p>
          <b>Cheque Type:</b> {selectedCheque?.cheque_type}
        </p>
        <Select
          placeholder="Select Status"
          style={{ width: "100%", marginBottom: "10px" }}
          value={status}
          onChange={(v) => setStatus(v)}
          options={[
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
          ]}
        />
        <Select
          placeholder="Select Account"
          style={{ width: "100%" }}
          value={account}
          onChange={(v) => setAccount(v)}
          allowClear
          loading={accountsLoading}
          options={accounts.map((acc) => ({
            value: acc.acc_id,
            label: acc.acc_name,
          }))}
        />
      </Modal>

      {/* View Voucher Drawer */}
      <Drawer
        title={`Cheque Voucher${viewCheque?.voucher_no ? ` - ${viewCheque.voucher_no}` : ""}`}
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        width={900}
        extra={
          <Space>
            <Button
              icon={<FilePdfOutlined />}
              type="primary"
              loading={voucherPdfLoading}
              onClick={handleVoucherDownload}
            >
              Download
            </Button>
            <Button
              icon={<PrinterOutlined />}
              type="primary"
              onClick={handleVoucherPrint}
            >
              Print
            </Button>
          </Space>
        }
      >
        <div ref={voucherViewRef} style={{ width: "fit-content" }}>
          {viewCheque && <ChequeVoucherBody chequeId={viewCheque.id} />}
        </div>
      </Drawer>

      {/* Hidden Voucher Print */}
      {viewCheque && (
        <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
          <ReportDom
            ref={voucherReportRef}
            mode="no-title"
            renderTable={() => (
              <ChequeVoucherBody chequeId={viewCheque.id} printMode />
            )}
          />
        </div>
      )}
    </div>
  );
};

export default Cheques;
